/**
 * QRAM risk vocabulary — the single source of truth for how risk levels and
 * modules are named, ordered and coloured in every QRAM output.
 *
 * THIS FILE EXISTS IN TWO PLACES, BYTE-FOR-BYTE IDENTICAL:
 *   backend/src/lib/riskVocabulary.ts
 *   frontend/src/lib/riskVocabulary.ts
 * The backend and frontend are separate packages with no shared workspace, so
 * the file is duplicated deliberately and a parity test
 * (backend/src/lib/riskVocabulary.test.ts) fails the build if they drift.
 * Edit one, copy it to the other.
 *
 * Why this exists (Sprint 1, defects D1–D4): the dashboard, the overview PDF
 * and the run report each carried their own hand-copied label, colour and
 * "score" tables. They drifted — two scales (0–10 vs 0–100) appeared in one
 * document, ordinal levels were averaged and re-coloured with thresholds that
 * did not match the engine's bands, and the composites were described
 * inconsistently. Nothing in this file computes a score: scores and bands
 * come from the engine (asset_risk_result.normalized_score / risk_level) and
 * from the module's own configured thresholds. This file only names them.
 *
 * Pure data and pure functions only — no imports — so it can live in both
 * packages unchanged.
 */

export type RiskLevelCode =
  | 'Critical'
  | 'Very_high'
  | 'High'
  | 'Moderate'
  | 'Low'
  | 'Very_low'
  | 'No_risk'
  | 'NA';

/** Severity order, most severe first. */
export const RISK_LEVEL_ORDER: readonly RiskLevelCode[] = [
  'Critical',
  'Very_high',
  'High',
  'Moderate',
  'Low',
  'Very_low',
  'No_risk',
  'NA',
];

/** Levels that count as "High or above" in every KPI and headline. */
export const HIGH_PLUS_LEVELS: readonly RiskLevelCode[] = ['Critical', 'Very_high', 'High'];

export const RISK_LEVEL_LABEL: Readonly<Record<RiskLevelCode, string>> = {
  Critical: 'Critical',
  Very_high: 'Very high',
  High: 'High',
  Moderate: 'Moderate',
  Low: 'Low',
  Very_low: 'Very low',
  No_risk: 'No risk',
  NA: 'Not assessed',
};

/** Foreground colour per level (WCAG AA on white for text use). */
export const RISK_LEVEL_COLOR: Readonly<Record<RiskLevelCode, string>> = {
  Critical: '#7f1d1d',
  Very_high: '#b91c1c',
  High: '#c2410c',
  Moderate: '#a16207',
  Low: '#4d7c0f',
  Very_low: '#15803d',
  No_risk: '#4b5563',
  NA: '#64748b',
};

/** Light tint per level, for badge and banner backgrounds. */
export const RISK_LEVEL_TINT: Readonly<Record<RiskLevelCode, string>> = {
  Critical: '#fef2f2',
  Very_high: '#fef2f2',
  High: '#fff7ed',
  Moderate: '#fefce8',
  Low: '#f7fee7',
  Very_low: '#f0fdf4',
  No_risk: '#f8fafc',
  NA: '#f1f5f9',
};

export function isRiskLevelCode(v: unknown): v is RiskLevelCode {
  return typeof v === 'string' && (RISK_LEVEL_ORDER as readonly string[]).includes(v);
}

export function riskLevelLabel(level: string | null | undefined): string {
  return isRiskLevelCode(level) ? RISK_LEVEL_LABEL[level] : '—';
}

export function riskLevelColor(level: string | null | undefined): string {
  return isRiskLevelCode(level) ? RISK_LEVEL_COLOR[level] : RISK_LEVEL_COLOR.NA;
}

export function riskLevelTint(level: string | null | undefined): string {
  return isRiskLevelCode(level) ? RISK_LEVEL_TINT[level] : RISK_LEVEL_TINT.NA;
}

/** Rank for sorting: higher = more severe. NA ranks lowest. */
export function riskLevelRank(level: string | null | undefined): number {
  if (!isRiskLevelCode(level)) return -1;
  return RISK_LEVEL_ORDER.length - 1 - RISK_LEVEL_ORDER.indexOf(level);
}

export function isHighPlus(level: string | null | undefined): boolean {
  return isRiskLevelCode(level) && HIGH_PLUS_LEVELS.includes(level);
}

// ── Modules ────────────────────────────────────────────────────────────────

export type ModuleCodeName =
  | 'timeline'
  | 'data_risk'
  | 'supply_chain'
  | 'migration_feasibility'
  | 'threat'
  | 'cryptography'
  | 'compliance'
  | 'integrated_foundation'
  | 'integrated_advanced';

export type ModuleTier = 'foundation' | 'advanced' | 'composite';

export interface ModuleVocabulary {
  code: ModuleCodeName;
  /** Full display name. */
  label: string;
  /** Short name for chart axes and narrow columns. */
  short: string;
  tier: ModuleTier;
  /** One-sentence description of what the score measures. */
  description: string;
}

/**
 * Composite names (Step 2 terminology, Oct 2026):
 *   integrated_advanced   → "Overall risk": all seven dimensions; drives priority.
 *   integrated_foundation → "Baseline risk (business exposure)": Timeline, Data,
 *                           Supply chain and Migration feasibility.
 * A third view, "Technical exposure" (Threat, Cryptography, Compliance with
 * their Overall weights re-weighted), is computed by the insight engine; it is
 * named by TECHNICAL_EXPOSURE below because it is not a stored module.
 * The module CODES are unchanged (they are persisted in every run result);
 * only what a reader sees changed. Descriptions state each composite's actual
 * inputs and never quote weights: weights are configuration and are read from
 * the run (summary composite_weights), so a label cannot misstate them.
 */
export const MODULES: readonly ModuleVocabulary[] = [
  {
    code: 'timeline',
    label: 'Timeline',
    short: 'Timeline',
    tier: 'foundation',
    description: 'How the asset’s remaining service life and migration time compare with the expected arrival of a cryptographically relevant quantum computer.',
  },
  {
    code: 'data_risk',
    label: 'Data Risk',
    short: 'Data',
    tier: 'foundation',
    description: 'Sensitivity, regulatory weight and harvest-now-decrypt-later (HNDL) exposure of the data the asset holds or processes.',
  },
  {
    code: 'supply_chain',
    label: 'Supply Chain',
    short: 'Supply chain',
    tier: 'foundation',
    description: 'Dependence on vendors and their quantum-safe readiness; applies to vendor-supplied assets only.',
  },
  {
    code: 'migration_feasibility',
    label: 'Migration Feasibility',
    short: 'Migration',
    tier: 'foundation',
    description: 'How hard the asset is to migrate: cost, complexity, legacy constraints and crypto agility.',
  },
  {
    code: 'threat',
    label: 'Threat',
    short: 'Threat',
    tier: 'advanced',
    description: 'NIST SP 800-30 threat analysis over the quantum threat scenarios that apply to the asset type.',
  },
  {
    code: 'cryptography',
    label: 'Cryptography',
    short: 'Crypto',
    tier: 'advanced',
    description: 'Quantum vulnerability of the cryptographic artifacts inventoried for the asset (worst artifact sets the level).',
  },
  {
    code: 'compliance',
    label: 'Compliance',
    short: 'Compliance',
    tier: 'advanced',
    description: 'Share of applicable rules violated across the policies assigned to the project.',
  },
  {
    code: 'integrated_foundation',
    label: 'Baseline risk (business exposure)',
    short: 'Baseline',
    tier: 'composite',
    description: 'Weighted composite of Timeline, Data Risk, Supply Chain (vendor-supplied assets only) and Migration Feasibility; the weights are re-weighted over the dimensions that apply to each asset.',
  },
  {
    code: 'integrated_advanced',
    label: 'Overall risk',
    short: 'Overall',
    tier: 'composite',
    description: 'Weighted composite of all seven dimensions (Timeline, Data Risk, Supply Chain, Migration Feasibility, Threat, Cryptography and Compliance) that drives priority; the weights are re-weighted over the dimensions that apply to each asset.',
  },
];

/** The insight engine's third view. Not a stored module, so it has no code. */
export const TECHNICAL_EXPOSURE = {
  label: 'Technical exposure',
  short: 'Technical',
  description: 'Threat, Cryptography and Compliance only, with their Overall risk weights re-weighted over the ones that apply to each asset.',
} as const;

/** Group labels for the dimension tiers (display only; the tier codes stay). */
export const TIER_LABEL: Readonly<Record<ModuleTier, string>> = {
  foundation: 'Baseline risk (business exposure)',
  advanced: 'Technical exposure',
  composite: 'Overall risk',
};

/** Canonical display order: composites first, then the Baseline dimensions, then the Technical exposure dimensions. */
export const MODULE_DISPLAY_ORDER: readonly ModuleCodeName[] = [
  'integrated_advanced',
  'integrated_foundation',
  'timeline',
  'data_risk',
  'supply_chain',
  'migration_feasibility',
  'threat',
  'cryptography',
  'compliance',
];

export const FOUNDATION_MODULES: readonly ModuleCodeName[] = MODULES.filter((m) => m.tier === 'foundation').map((m) => m.code);
export const ADVANCED_MODULES: readonly ModuleCodeName[] = MODULES.filter((m) => m.tier === 'advanced').map((m) => m.code);
export const COMPOSITE_MODULES: readonly ModuleCodeName[] = MODULES.filter((m) => m.tier === 'composite').map((m) => m.code);

export function moduleVocabulary(code: string): ModuleVocabulary | undefined {
  return MODULES.find((m) => m.code === code);
}

/** Full label; unknown codes are humanised rather than dropped. */
export function moduleLabel(code: string): string {
  return moduleVocabulary(code)?.label ?? humanise(code);
}

export function moduleShortLabel(code: string): string {
  return moduleVocabulary(code)?.short ?? humanise(code);
}

/** Sort module codes into canonical display order; unknown codes go last, in input order. */
export function orderModules(codes: readonly string[]): string[] {
  const known = MODULE_DISPLAY_ORDER.filter((c) => codes.includes(c));
  const unknown = codes.filter((c) => !(MODULE_DISPLAY_ORDER as readonly string[]).includes(c));
  return [...known, ...unknown];
}

function humanise(code: string): string {
  return code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Scores and bands ───────────────────────────────────────────────────────

export interface RiskBand {
  code: string;
  min: number;
  max: number;
}

/**
 * Map an engine score (0–100) to a level using a module's OWN configured bands
 * (module_config.risk_thresholds.levels). Mirrors the engine's thresholdLookup:
 * min inclusive, max exclusive, a score of 100 falls in the highest band.
 * Returns null when no bands are supplied — callers must not invent bands.
 */
export function bandForScore(score: number, bands: readonly RiskBand[] | null | undefined): RiskLevelCode | null {
  if (!bands || bands.length === 0 || !Number.isFinite(score)) return null;
  const clamped = Math.max(0, Math.min(100, score));
  for (const b of bands) {
    if (clamped >= b.min && clamped < b.max && isRiskLevelCode(b.code)) return b.code;
  }
  const last = bands[bands.length - 1];
  return last && isRiskLevelCode(last.code) ? last.code : null;
}

/** Engine scores are 0–100. Format consistently everywhere: one decimal, no "/10". */
export function formatScore(score: number | null | undefined): string {
  return score == null || !Number.isFinite(score) ? '—' : score.toFixed(1);
}

export function formatPct(numerator: number, denominator: number): string {
  if (!denominator) return '—';
  return `${Math.round((numerator / denominator) * 100)}%`;
}
